import React from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { X, GripVertical, Edit2, Check, XIcon } from 'lucide-react';

function SortableItem({ id, value, onDelete, onRename }) {
    const [isEditing, setIsEditing] = React.useState(false);
    const [editValue, setEditValue] = React.useState(value);

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const handleSaveEdit = () => {
        if (editValue.trim() && editValue !== value) {
            onRename(value, editValue.trim());
        }
        setIsEditing(false);
    };

    const handleCancelEdit = () => {
        setEditValue(value);
        setIsEditing(false);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSaveEdit();
        } else if (e.key === 'Escape') {
            handleCancelEdit();
        }
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`flex justify-between items-center text-sm p-2 hover:bg-gray-50 rounded border ${
                isDragging ? 'shadow-lg' : ''
            }`}
        >
            <div className="flex items-center gap-2 flex-1">
                <div
                    {...attributes}
                    {...listeners}
                    className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
                >
                    <GripVertical size={16} />
                </div>

                {isEditing ? (
                    <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={handleKeyPress}
                        onBlur={handleCancelEdit}
                        className="flex-1 px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        autoFocus
                    />
                ) : (
                    <span className="flex-1">{value}</span>
                )}
            </div>

            <div className="flex items-center gap-1">
                {isEditing ? (
                    <>
                        <button
                            onMouseDown={handleSaveEdit}
                            className="text-green-600 opacity-75 hover:opacity-100"
                            title="Save"
                        >
                            <Check size={14} />
                        </button>
                        <button
                            onMouseDown={handleCancelEdit}
                            className="text-gray-500 opacity-75 hover:opacity-100"
                            title="Cancel"
                        >
                            <XIcon size={14} />
                        </button>
                    </>
                ) : (
                    <>
                        <button
                            onClick={() => setIsEditing(true)}
                            className="text-blue-500 opacity-50 hover:opacity-100"
                            title="Rename"
                        >
                            <Edit2 size={14} />
                        </button>
                        <button
                            onClick={() => onDelete(value)}
                            className="text-red-500 opacity-50 hover:opacity-100"
                            title="Delete"
                        >
                            <X size={14} />
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

const SortableDropdownList = ({ data, title, onDelete, onReorder, onAdd, onRename }) => {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    function handleDragEnd(event) {
        const { active, over } = event;

        if (active.id !== over.id) {
            const oldIndex = data.indexOf(active.id);
            const newIndex = data.indexOf(over.id);
            const newOrder = arrayMove(data, oldIndex, newIndex);
            onReorder(newOrder);
        }
    }

    return (
        <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h3 className="font-semibold mb-2">{title}</h3>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext items={data} strategy={verticalListSortingStrategy}>
                    <div className="space-y-1">
                        {data.map(item => (
                            <SortableItem
                                key={item}
                                id={item}
                                value={item}
                                onDelete={onDelete}
                                onRename={onRename}
                            />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>
            <button
                onClick={onAdd}
                className="mt-2 text-sm text-blue-600 hover:underline"
            >
                Add New
            </button>
        </div>
    );
};

export default SortableDropdownList;